import { companyApi } from 'api';
import { makeAutoObservable, runInAction } from 'mobx';
import type { Company } from 'types/api';
import { personalDataStore } from 'stores/personalDataStore';

export type NewCompany = {
  email: string;
  inn: string;
  name: string;
};

export class CompanyStore {
  newCompany: NewCompany = {
    email: '',
    inn: '',
    name: ''
  };

  currentCompany: Company | null = null;

  loading = false;

  error: string | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setNewCompanyField<K extends keyof NewCompany>(field: K, value: NewCompany[K]): void {
    this.newCompany[field] = value;
  }

  setNewCompany(data: Partial<NewCompany>): void {
    this.newCompany = { ...this.newCompany, ...data };
  }

  async createCompany(): Promise<Company> {
    const company = await companyApi.createCompany(this.newCompany);
    runInAction(() => {
      this.currentCompany = company;
    });
    return company;
  }

  async loadCompany(id: string): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const company = await companyApi.findOne(id);
      runInAction(() => {
        this.currentCompany = company;
      });
    } catch (err: unknown) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Не удалось загрузить компанию';
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async updateCompany(id: string, dto: { name?: string; email?: string; inn?: string }): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const company = await companyApi.update(id, dto);
      runInAction(() => {
        this.currentCompany = company;
      });
      await personalDataStore.refreshUser();
    } catch (err: unknown) {
      runInAction(() => {
        this.error = 'Не удалось обновить информацию о компании';
      });
      throw err;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async removeCompany(id: string): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      await companyApi.remove(id);
      runInAction(() => {
        this.currentCompany = null;
      });
      await personalDataStore.refreshUser();
    } catch (err: unknown) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Не удалось удалить компанию';
      });
      throw err;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async uploadLogo(id: string, file: File): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const company = await companyApi.uploadLogo(id, file);
      runInAction(() => {
        this.currentCompany = company;
      });
      await personalDataStore.refreshUser();
    } catch (err: unknown) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Не удалось загрузить логотип';
      });
      throw err;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  clearCurrentCompany(): void {
    this.currentCompany = null;
    this.error = null;
  }

  clearError(): void {
    this.error = null;
  }
}

export const companyStore = new CompanyStore();
